import { z } from "zod";
import type {
  ConsolidationTheme,
  DiscussionArcPoint,
  RecapSection,
  StoredDiscussionArcPoint,
} from "../types.ts";
import {
  MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS,
  MAX_RECAP_CONSOLIDATION_NARRATIVE_ITEMS,
  MAX_RECAP_CONSOLIDATION_THEME_ITEMS,
} from "./limits.ts";

export const panelRecapResponseSchema = z.object({
  keyPoints: z.array(z.string()).max(10),
  examples: z.array(z.string()).max(10),
  differentViews: z.array(z.string()).max(10),
});

export type PanelRecapObject = z.infer<typeof panelRecapResponseSchema>;

export const recapPointSchema = z.object({
  claimId: z.string().optional(),
  text: z.string(),
  subpoints: z.array(z.string()),
});

const recapPointResponseSchema = z.object({
  text: z.string(),
  subpoints: z.array(z.string()),
});

export const episodeClaimResponseSchema = z.object({
  text: z.string(),
  subpoints: z.array(z.string()).max(4),
  speaker: z.string(),
  relationKind: z.enum(["new", "extends", "candidate_repeat", "qualifies"]),
  targetClaimId: z.string(),
});

export const episodeArtifactResponseSchema = z.object({
  nonSubstantive: z.boolean(),
  headline: z.string(),
  claims: z.array(episodeClaimResponseSchema).max(3),
});

export type EpisodeArtifactResponseObject = z.infer<typeof episodeArtifactResponseSchema>;

export const recapSectionSchema = z.object({
  timeTs: z.number(),
  timeLabel: z.string(),
  speaker: z.string(),
  speakerKey: z.string().nullable(),
  points: z.array(recapPointSchema),
});

const recapSectionResponseItemSchema = z.object({
  timeTs: z.number(),
  timeLabel: z.string(),
  speaker: z.string(),
  speakerKey: z.string().nullable(),
  points: z.array(recapPointResponseSchema),
});

export const recapTensionSchema = z.object({
  description: z.string(),
  speakers: z.array(z.string()).max(8),
  timeRange: z.string(),
  rangeStartTs: z.number(),
  rangeEndTs: z.number(),
});

export const consolidationThemeTensionSchema = z.object({
  description: z.string(),
  speakers: z.array(z.string()).max(8),
});

export const consolidationThemeSchema = z.object({
  title: z.string(),
  synthesis: z.string(),
  speakers: z.array(z.string()).max(8),
  tensions: z.array(consolidationThemeTensionSchema).max(5),
  themeId: z.string().optional(),
  mergedFromThemeIds: z.array(z.string()).optional(),
  projectorSummary: z.string().optional(),
  projectorFirstSeenAt: z.number().optional(),
  projectorLastChangedAt: z.number().optional(),
  projectorRevision: z.number().optional(),
});

// LLM response contract: identity fields are REQUIRED so strict structured
// outputs force the model to fill them ("new" / empty array when unused).
export const consolidationThemeResponseSchema = z.object({
  title: z.string(),
  synthesis: z.string(),
  speakers: z.array(z.string()).max(8),
  tensions: z.array(consolidationThemeTensionSchema).max(5),
  themeId: z.string(),
  mergedFromThemeIds: z.array(z.string()),
  projectorSummary: z.string(),
});

export const discussionArcPointSchema = z.object({
  point: z.string(),
  subpoints: z.array(z.string()).max(MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS),
});

export const panelRecapSectionResponseSchema = z.object({
  sections: z.array(recapSectionResponseItemSchema).max(20),
});

export const recapConsolidationResponseSchema = z.object({
  narrative: z.array(discussionArcPointSchema).max(MAX_RECAP_CONSOLIDATION_NARRATIVE_ITEMS),
  themes: z.array(consolidationThemeResponseSchema).max(MAX_RECAP_CONSOLIDATION_THEME_ITEMS),
  // Selection list, not content: over-proposal is cosmetic (the server slices
  // to the display limit losslessly), so the request-side cap only bounds
  // runaway output — a 7th proposed id must never hard-fail a valid snapshot.
  projectorThemeIds: z.array(z.string()).max(MAX_RECAP_CONSOLIDATION_THEME_ITEMS),
});

export type PanelRecapSectionResponseObject = z.infer<typeof panelRecapSectionResponseSchema>;
export type RecapConsolidationResponseObject = z.infer<typeof recapConsolidationResponseSchema>;
type NormalizableRecapPoint = {
  claimId?: string;
  text: string;
  subpoints: string[];
};
type NormalizableRecapSection = {
  timeTs: number;
  timeLabel: string;
  speaker: string;
  speakerKey?: string | null;
  sourceEntryIds?: string[];
  sourceSpans?: Array<{
    transcriptEntryId: string;
    startOffset: number;
    endOffset: number;
  }>;
  points: NormalizableRecapPoint[];
};
type NormalizableConsolidationThemeTension = {
  description: string;
  speakers: string[];
};
type NormalizableConsolidationTheme = {
  title: string;
  synthesis: string;
  speakers: string[];
  tensions: NormalizableConsolidationThemeTension[];
  themeId?: string;
  mergedFromThemeIds?: string[];
  projectorSummary?: string;
  projectorFirstSeenAt?: number;
  projectorLastChangedAt?: number;
  projectorRevision?: number;
};

export function normalizePanelRecapObject(
  recap: PanelRecapObject,
  limits: {
    maxKeyPoints: number;
    maxExamples: number;
    includeDifferentViews: boolean;
  },
): PanelRecapObject {
  return {
    keyPoints: normalizeLines(recap.keyPoints).slice(0, limits.maxKeyPoints),
    examples: normalizeLines(recap.examples).slice(0, limits.maxExamples),
    differentViews: limits.includeDifferentViews
      ? normalizeLines(recap.differentViews).slice(0, Math.max(0, limits.maxKeyPoints))
      : [],
  };
}

export function normalizePanelRecapSectionResponse(
  recap: { sections: NormalizableRecapSection[] },
): { sections: RecapSection[] } {
  return {
    sections: recap.sections
      .map(normalizeSection)
      .filter((section): section is RecapSection => section !== null),
  };
}

export function normalizeRecapConsolidationResponse(
  recap: {
    narrative: StoredDiscussionArcPoint[];
    themes: NormalizableConsolidationTheme[];
    projectorThemeIds?: string[];
  },
): { narrative: DiscussionArcPoint[]; themes: ConsolidationTheme[]; projectorThemeIds?: string[] } {
  return {
    narrative: normalizeDiscussionArcPoints(recap.narrative),
    themes: recap.themes
      .map(normalizeTheme)
      .filter((theme): theme is ConsolidationTheme => theme !== null)
      .slice(0, MAX_RECAP_CONSOLIDATION_THEME_ITEMS),
    ...(recap.projectorThemeIds ? { projectorThemeIds: recap.projectorThemeIds.slice(0, 6) } : {}),
  };
}

export function normalizeDiscussionArcPoints(
  narrative: StoredDiscussionArcPoint[],
): DiscussionArcPoint[] {
  const seen = new Set<string>();
  const result: DiscussionArcPoint[] = [];
  for (const item of narrative) {
    const point = normalizeText(typeof item === "string" ? item : item.point);
    if (!point) continue;
    const key = point.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push({
      point,
      subpoints: typeof item === "string"
        ? []
        : normalizeLines(Array.isArray(item.subpoints) ? item.subpoints : [])
          .slice(0, MAX_RECAP_CONSOLIDATION_ARC_SUBPOINTS),
    });
    if (result.length >= MAX_RECAP_CONSOLIDATION_NARRATIVE_ITEMS) break;
  }
  return result;
}

export function sectionsToLegacyRecap(sections: RecapSection[]): PanelRecapObject {
  const keyPoints = normalizeLines(
    sections.flatMap((section) =>
      section.points.map((point) => `${section.speaker}: ${point.text}`),
    ),
  ).slice(0, 10);
  const examples = normalizeLines(
    sections.flatMap((section) =>
      section.points.flatMap((point) =>
        point.subpoints.map((subpoint) => `${section.speaker}: ${subpoint}`),
      ),
    ),
  ).slice(0, 10);

  return {
    keyPoints,
    examples: examples.length > 0 ? examples : keyPoints.slice(0, 3),
    differentViews: [],
  };
}

function normalizeSection(section: NormalizableRecapSection): RecapSection | null {
  const speaker = normalizeText(section.speaker);
  const timeLabel = normalizeText(section.timeLabel);
  const points = section.points
    .map((point) => ({
      ...(point.claimId?.trim() ? { claimId: point.claimId.trim() } : {}),
      text: normalizeText(point.text),
      subpoints: normalizeLines(point.subpoints).slice(0, 8),
    }))
    .filter((point) => point.text);

  if (!speaker || !timeLabel || points.length === 0) return null;

  return {
    timeTs: section.timeTs,
    timeLabel,
    speaker,
    ...(section.speakerKey?.trim() ? { speakerKey: section.speakerKey.trim() } : {}),
    ...(section.sourceEntryIds?.length
      ? { sourceEntryIds: [...new Set(section.sourceEntryIds)] }
      : {}),
    ...(section.sourceSpans?.length
      ? {
          sourceSpans: section.sourceSpans.filter((span) =>
            span.endOffset > span.startOffset && span.startOffset >= 0,
          ),
        }
      : {}),
    points,
  };
}

function normalizeTheme(theme: NormalizableConsolidationTheme): ConsolidationTheme | null {
  const title = normalizeText(theme.title);
  const synthesis = normalizeText(theme.synthesis);
  if (!title || !synthesis) return null;
  return {
    title,
    synthesis,
    speakers: normalizeLines(theme.speakers).slice(0, 8),
    tensions: theme.tensions
      .map(normalizeThemeTension)
      .filter((tension): tension is ConsolidationTheme["tensions"][number] => tension !== null)
      .slice(0, 5),
    ...(theme.themeId ? { themeId: theme.themeId } : {}),
    ...(theme.mergedFromThemeIds?.length ? { mergedFromThemeIds: theme.mergedFromThemeIds } : {}),
    ...(theme.projectorSummary?.trim() ? { projectorSummary: theme.projectorSummary.trim() } : {}),
    ...(theme.projectorFirstSeenAt !== undefined
      ? { projectorFirstSeenAt: theme.projectorFirstSeenAt }
      : {}),
    ...(theme.projectorLastChangedAt !== undefined
      ? { projectorLastChangedAt: theme.projectorLastChangedAt }
      : {}),
    ...(theme.projectorRevision !== undefined
      ? { projectorRevision: theme.projectorRevision }
      : {}),
  };
}

function normalizeThemeTension(
  tension: NormalizableConsolidationThemeTension,
): ConsolidationTheme["tensions"][number] | null {
  const description = normalizeText(tension.description);
  const speakers = normalizeLines(tension.speakers).slice(0, 8);
  if (!description) return null;
  return {
    description,
    speakers,
  };
}

function normalizeLines(lines: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const line of lines) {
    const normalized = normalizeText(line);
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(normalized);
  }
  return result;
}

function normalizeText(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}
